import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { useBooking } from '../../contexts/BookingContext';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { format } from 'date-fns';
import {
  BedDouble,
  MapPin,
  Users,
  Calendar,
  ArrowLeft,
} from 'lucide-react';

export default function SearchResults() {
  const navigate = useNavigate();
  const { searchParams, searchResults, setSelectedRoom } = useBooking();

  useEffect(() => {
    if (!searchResults) {
      navigate('/');
    }
  }, [searchResults, navigate]);

  const handleBookRoom = (room) => {
    setSelectedRoom(room);
    navigate('/checkout');
  };

  if (!searchResults) {
    return null;
  }

  return (
    <PublicLayout>
      <div className="py-12" data-testid="search-results-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
              <ArrowLeft className="h-4 w-4" />
              New Search
            </Link>
            <h1 className="text-3xl font-serif font-medium mb-4">Available Rooms</h1>
            <div className="flex items-center gap-4 text-muted-foreground flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {format(new Date(searchResults.check_in), 'MMM dd, yyyy')} -{' '}
                  {format(new Date(searchResults.check_out), 'MMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>{searchResults.guests} guest(s)</span>
              </div>
              <Badge variant="outline">
                {searchResults.nights} night(s)
              </Badge>
            </div>
          </div>

          {/* Results */}
          {searchResults.rooms.length === 0 ? (
            <div className="bg-surface rounded-xl p-16 text-center">
              <BedDouble className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
              <h3 className="text-xl font-semibold mb-2">No rooms available</h3>
              <p className="text-muted-foreground mb-6">
                We couldn&apos;t find any available rooms for your selected dates.
              </p>
              <Button onClick={() => navigate('/')}>Try Different Dates</Button>
            </div>
          ) : (
            <div className="space-y-6">
              {searchResults.rooms.map((room) => (
                <div
                  key={room.id}
                  className="bg-white border border-border rounded-2xl overflow-hidden hover:shadow-float transition-shadow"
                  data-testid={`search-room-${room.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="w-full md:w-72 h-48 md:h-auto flex-shrink-0">
                      {room.image_url ? (
                        <img
                          src={room.image_url}
                          alt={room.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <BedDouble className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 p-6">
                      <div className="flex flex-col h-full">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-semibold mb-1">{room.name}</h3>
                            <div className="flex items-center gap-1 text-muted-foreground text-sm">
                              <MapPin className="h-4 w-4" />
                              <span>{room.property_name} • {room.property_city}</span>
                            </div>
                          </div>
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            {room.available_count} left
                          </Badge>
                        </div>

                        {room.description && (
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                            {room.description}
                          </p>
                        )}

                        <div className="flex items-center gap-3 mb-4 flex-wrap">
                          <div className="flex items-center gap-1 text-sm">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span>Up to {room.max_occupancy}</span>
                          </div>
                          {room.amenities?.slice(0, 4).map((amenity) => (
                            <Badge key={amenity} variant="secondary" className="text-xs">
                              {amenity}
                            </Badge>
                          ))}
                        </div>

                        <div className="flex items-end justify-between mt-auto pt-4 border-t border-border">
                          <div>
                            <p className="text-sm text-muted-foreground">
                              {formatCurrency(room.price_per_night, room.currency || 'USD')} × {searchResults.nights} night(s)
                            </p>
                            <p className="text-2xl font-semibold text-primary">
                              {formatCurrency(room.total_price, room.currency || 'USD')}
                            </p>
                          </div>
                          <Button
                            size="lg"
                            onClick={() => handleBookRoom(room)}
                            data-testid={`select-room-${room.id}`}
                          >
                            Select Room
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
